import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Flag, MessageCircle, RefreshCw, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import BackgroundBlobs from '../components/BackgroundBlobs';
import { api, isNetworkOrTimeoutError } from '../lib/api';
import { resolveProfilePic, handleProfilePicError } from '../lib/avatar';

const LOW_NETWORK_MESSAGE = 'Low network. Tap to retry.';

// Turn any load failure into a user-friendly message. Raw axios/timeout strings
// (e.g. "timeout of 15000ms exceeded") are never shown to users.
const toFriendlyLoadError = (error) => {
  if (isNetworkOrTimeoutError(error)) return LOW_NETWORK_MESSAGE;
  return error?.response?.data?.detail || 'Unable to load chat. Tap to retry.';
};

const getChatUserName = (chatUser) => chatUser?.display_name || chatUser?.name || 'Harday user';
const getChatUserAvatar = (chatUser) => chatUser?.profile_photo || chatUser?.avatar || chatUser?.profilePic;
const normalizeOtherUserId = (value) => {
  const id = typeof value === 'string' ? value.trim() : '';
  return id || null;
};

const formatTime = (value) => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
  } catch {
    return '';
  }
};

const chatListStyle = {
  paddingBottom: 'calc(7rem + env(safe-area-inset-bottom, 0px))',
};

const MessagesPage = () => {
  const { user, loading, isAuthenticated } = useAuth();
  const { otherUserId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [thread, setThread] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [status, setStatus] = useState('');
  const [loadError, setLoadError] = useState('');
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [sending, setSending] = useState(false);
  const initialLoadKeyRef = useRef(null);
  const loadRef = useRef(null);
  const autoRetriedRef = useRef(false);
  const targetUserId = normalizeOtherUserId(otherUserId);

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate('/login');
  }, [isAuthenticated, loading, navigate]);

  const loadConversations = useCallback(async ({ showLoading = false } = {}) => {
    if (!user?.id) return;
    if (showLoading) setIsPageLoading(true);
    try {
      const { data } = await api.getChats(user.id);
      // A failed load never reaches here (it throws), so cached chats are only
      // ever replaced by a real, successful response — not wiped on a timeout.
      setConversations(Array.isArray(data?.conversations) ? data.conversations : []);
      setLoadError('');
      setStatus('');
    } finally {
      if (showLoading) setIsPageLoading(false);
    }
  }, [user?.id]);

  const loadThread = useCallback(async ({ showLoading = false } = {}) => {
    if (!user?.id || !targetUserId) return;
    if (targetUserId === user.id) {
      setThread(null);
      setStatus('You cannot message yourself.');
      return;
    }
    if (showLoading) setIsPageLoading(true);
    try {
      const { data } = await api.getChatThread(user.id, targetUserId);
      setThread(data);
      setLoadError('');
      setStatus('');
    } finally {
      if (showLoading) setIsPageLoading(false);
    }
  }, [targetUserId, user?.id]);

  useEffect(() => {
    if (!user?.id) return undefined;

    const loadKey = targetUserId ? `thread:${user.id}:${targetUserId}` : `conversations:${user.id}`;
    const isNewChatView = initialLoadKeyRef.current !== loadKey;
    initialLoadKeyRef.current = loadKey;

    const load = targetUserId ? loadThread : loadConversations;
    // Expose the active loader so the Retry button and reconnect handler can
    // re-run whichever view (chat list or thread) is currently on screen.
    loadRef.current = load;

    load({ showLoading: isNewChatView }).catch((error) => {
      if (isNewChatView) setIsPageLoading(false);
      // Cached chats/thread already in state are left untouched so the user sees
      // their previous conversations instead of an empty "No chats yet" screen.
      setLoadError(toFriendlyLoadError(error));
    });

    const timer = setInterval(() => {
      load({ showLoading: false }).catch(() => {});
    }, targetUserId ? 7000 : 12000);

    return () => clearInterval(timer);
  }, [loadConversations, loadThread, targetUserId, user?.id]);

  const retryLoad = useCallback(() => {
    const load = loadRef.current;
    if (!load || isRetrying) return;
    setIsRetrying(true);
    load({ showLoading: false })
      .catch((error) => setLoadError(toFriendlyLoadError(error)))
      .finally(() => setIsRetrying(false));
  }, [isRetrying]);

  // Auto-retry once when the device comes back online. The one-shot guard (reset
  // on every successful load) prevents an infinite retry loop while still offline.
  useEffect(() => {
    if (!loadError) {
      autoRetriedRef.current = false;
      return undefined;
    }
    const handleOnline = () => {
      if (autoRetriedRef.current) return;
      autoRetriedRef.current = true;
      retryLoad();
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [loadError, retryLoad]);

  const otherUser = useMemo(() => thread?.otherUser || thread?.conversation?.otherUser, [thread]);
  const canUseThread = Boolean(targetUserId && otherUser?.id && otherUser.id !== user?.id);

  const sendMessage = async (event) => {
    event.preventDefault();
    const text = messageText.trim();
    if (!text || !user?.id || !canUseThread || sending) return;
    setSending(true);
    setStatus('');
    try {
      await api.sendMessage({ senderId: user.id, receiverId: otherUser.id, text });
      setMessageText('');
      await loadThread({ showLoading: false });
    } catch (error) {
      setStatus(isNetworkOrTimeoutError(error)
        ? 'Low network. Message not sent, please try again.'
        : (error?.response?.data?.detail || 'Unable to send message.'));
    } finally {
      setSending(false);
    }
  };

  const reportChat = async () => {
    if (!user?.id || !canUseThread) return;
    try {
      await api.reportChat(user.id, otherUser.id, { reporterId: user.id, reason: 'User reported this chat' });
      setStatus('Chat reported. Harday team can review it.');
    } catch (error) {
      setStatus(isNetworkOrTimeoutError(error)
        ? 'Low network. Could not report chat, please try again.'
        : (error?.response?.data?.detail || 'Unable to report chat.'));
    }
  };

  if (loading || !isAuthenticated) return null;

  return (
    <div className="w-full max-w-md mx-auto min-h-screen relative overflow-hidden bg-white shadow-2xl">
      <BackgroundBlobs />
      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="webview-safe-top p-4 flex items-center gap-3 border-b border-white/50 bg-white/70 backdrop-blur-xl sticky top-0 z-20">
          <button type="button" onClick={() => (targetUserId ? navigate('/messages') : navigate('/dashboard'))} className="h-10 w-10 rounded-full bg-white/80 flex items-center justify-center">
            <ArrowLeft className="w-5 h-5" />
          </button>
          {targetUserId && otherUser ? (
            <div className="flex-1 min-w-0 flex items-center gap-3">
              <img src={resolveProfilePic(getChatUserAvatar(otherUser))} alt={getChatUserName(otherUser)} onError={handleProfilePicError} className="w-11 h-11 rounded-full object-cover border border-white" />
              <div className="min-w-0">
                <h1 className="font-['Outfit'] text-lg font-bold text-[#111827] truncate">{getChatUserName(otherUser)}</h1>
                {otherUser.username && <p className="text-xs text-[#6B7280] font-['DM_Sans'] truncate">{otherUser.username}</p>}
              </div>
            </div>
          ) : (
            <div className="flex-1">
              <h1 className="font-['Outfit'] text-xl font-bold text-[#111827]">{targetUserId ? 'Chat' : 'Messages'}</h1>
              <p className="text-xs text-[#6B7280] font-['DM_Sans']">{isPageLoading ? 'Loading chat...' : 'Text-only Harday chats'}</p>
            </div>
          )}
          {targetUserId && <button type="button" onClick={reportChat} disabled={!canUseThread} className="text-xs font-semibold text-red-500 flex items-center gap-1 disabled:opacity-40"><Flag className="w-4 h-4" /> Report</button>}
        </header>

        {loadError && (
          <button
            type="button"
            onClick={retryLoad}
            disabled={isRetrying}
            className="mx-4 mt-3 w-[calc(100%-2rem)] rounded-xl bg-white/80 p-3 flex items-center justify-between gap-3 text-left disabled:opacity-60"
          >
            <span className="text-sm font-semibold text-[#9333EA]">{loadError}</span>
            <span className="flex items-center gap-1 text-xs font-bold text-[#EC4899] whitespace-nowrap">
              <RefreshCw className={`w-4 h-4 ${isRetrying ? 'animate-spin' : ''}`} />
              {isRetrying ? 'Retrying...' : 'Retry'}
            </span>
          </button>
        )}
        {status && <p className="mx-4 mt-3 rounded-xl bg-white/80 p-3 text-sm font-semibold text-[#9333EA]">{status}</p>}
        {isPageLoading && !status && !loadError && <p className="mx-4 mt-3 rounded-xl bg-white/80 p-3 text-sm font-semibold text-[#6B7280]">Loading...</p>}

        {!targetUserId ? (
          <main className="p-4 space-y-3">
            {!isPageLoading && !loadError && conversations.length === 0 && <div className="glass-panel rounded-[18px] p-6 text-center"><MessageCircle className="w-10 h-10 mx-auto text-[#9333EA] mb-2" /><p className="font-semibold">No chats yet.</p><p className="text-sm text-[#6B7280]">Open a user or winner profile and tap Message.</p></div>}
            {conversations.filter((conversation) => conversation.otherUser?.id && conversation.otherUser.id !== user?.id).map((conversation) => (
              <button key={conversation.id} type="button" onClick={() => navigate(`/messages/${conversation.otherUser.id}`)} className="w-full glass-panel rounded-[18px] p-4 flex items-center gap-3 text-left">
                <img src={resolveProfilePic(getChatUserAvatar(conversation.otherUser))} alt="" onError={handleProfilePicError} className="w-12 h-12 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between gap-2"><p className="font-bold truncate">{getChatUserName(conversation.otherUser)}</p><span className="text-[10px] text-[#6B7280]">{formatTime(conversation.lastMessageAt)}</span></div>
                  {conversation.otherUser?.username && <p className="text-xs text-[#6B7280] truncate">{conversation.otherUser.username}</p>}
                  <p className="text-xs text-[#6B7280] truncate">{conversation.lastMessage?.text || 'Open chat'}</p>
                </div>
                {conversation.unreadCount > 0 && <span className="min-w-6 h-6 px-2 rounded-full bg-[#EC4899] text-white text-xs font-bold flex items-center justify-center">{conversation.unreadCount}</span>}
              </button>
            ))}
          </main>
        ) : (
          <>
            <main className="flex-1 p-4 space-y-3 overflow-y-auto pb-28" style={chatListStyle}>
              {(thread?.messages || []).map((message) => {
                const mine = message.senderId === user.id;
                return <div key={message.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}><div className={`max-w-[78%] rounded-2xl px-4 py-2 ${mine ? 'bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white' : 'bg-white/85 text-[#111827]'}`}><p className="text-sm whitespace-pre-wrap break-words">{message.text}</p><p className={`mt-1 text-[10px] ${mine ? 'text-white/75' : 'text-[#6B7280]'}`}>{mine ? 'You' : otherUser?.name || 'User'} • {formatTime(message.createdAt)} {mine && (message.readAt ? '• Read' : '• Unread')}</p></div></div>;
              })}
            </main>
            <form onSubmit={sendMessage} className="webview-chat-input-safe fixed bottom-0 left-0 right-0 max-w-md mx-auto p-3 bg-white/80 backdrop-blur-xl border-t border-white/50 flex gap-2">
              <input value={messageText} onChange={(e) => setMessageText(e.target.value)} disabled={!canUseThread} maxLength={1000} placeholder={canUseThread ? 'Type a message...' : 'Chat unavailable'} className="flex-1 rounded-full bg-white px-4 py-3 text-sm outline-none border border-[#E5E7EB] disabled:opacity-60" />
              <button type="submit" disabled={sending || !messageText.trim() || !canUseThread} className="h-12 w-12 rounded-full bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white flex items-center justify-center disabled:opacity-50"><Send className="w-5 h-5" /></button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
